import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, BookOpen, MessageSquare, Send, Filter, Info, Trash2, Edit, ClipboardList, Award, Video } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import MessageFiles from '../components/MessageFiles';
import LessonFiles from '../components/LessonFiles';
import RichTextEditor from '../components/RichTextEditor';
import FilePickerInput from '../components/FilePickerInput';
import YouTubeEmbed from '../components/YouTubeEmbed';
import { NotificationBell } from '../components/NotificationBell';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';
import MyAttendancePage from '../components/MyAttendancePage';
import MyCompetenciesPage from '../components/MyCompetenciesPage';
import { OnlineLessonsPanel } from '../components/online/OnlineLessonsPanel';
import { DirectDebitCard } from '../components/DirectDebitCard';
import LessonsTab from './student/LessonsTab';
import BoardTab from './student/BoardTab';
import PrivateTab from './student/PrivateTab';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFullName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.username || '';
};

export default function StudentDashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [boardMessages, setBoardMessages] = useState([]);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState('unpaid');
  const [recipients, setRecipients] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Board message form
  const [boardContent, setBoardContent] = useState('');
  const [boardFiles, setBoardFiles] = useState([]);
  const [boardFile, setBoardFile] = useState(null);

  // Private message form
  const [privateContent, setPrivateContent] = useState('');
  const [privateRecipient, setPrivateRecipient] = useState('');
  const [privateFiles, setPrivateFiles] = useState([]);

  // Filters
  const [lessonFilter, setLessonFilter] = useState('all');
  const [boardFilter, setBoardFilter] = useState('all');
  const [privateFilter, setPrivateFilter] = useState('all');

  // Edit message state
  const [editingMessage, setEditingMessage] = useState(null);
  const [editMessageOpen, setEditMessageOpen] = useState(false);
  const [editMessageContent, setEditMessageContent] = useState('');
  const [editMessageFilesToRemove, setEditMessageFilesToRemove] = useState(new Set());
  const [editMessageNewFiles, setEditMessageNewFiles] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [lessonsRes, messagesRes, paymentRes, recipientsRes, classesRes] = await Promise.all([
        axios.get(`${API_URL}/lessons`, { headers }),
        axios.get(`${API_URL}/messages`, { headers }),
        axios.get(`${API_URL}/payments/status/${user.user_id}`, { headers }),
        axios.get(`${API_URL}/messages/recipients`, { headers }),
        axios.get(`${API_URL}/classes`, { headers })
      ]);

      setLessons(lessonsRes.data);
      const allMessages = messagesRes.data;
      setBoardMessages(allMessages.filter(m => m.message_type === 'board'));
      setPrivateMessages(allMessages.filter(m => m.message_type === 'individual'));
      setPaymentStatus(paymentRes.data.status);
      setRecipients(recipientsRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Грешка при зареждане на данните');
    } finally {
      setLoading(false);
    }
  }, [token, user.user_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendBoardMessage = async () => {
    if (!boardContent.trim()) {
      toast.error('Моля, въведете съобщение');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('content', boardContent);
      formData.append('message_type', 'board');
      formData.append('class_id', user.class_ids[0]);
      
      if (boardFiles && boardFiles.length > 0) {
        Array.from(boardFiles).forEach(file => {
          formData.append('file', file);
        });
      }

      await axios.post(`${API_URL}/messages`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      toast.success('Съобщението е изпратено');
      setBoardContent('');
      setBoardFiles([]);
      fetchData();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Грешка при изпращане на съобщението');
    }
  };

  const handleSendPrivateMessage = async () => {
    if (!privateContent.trim()) {
      toast.error('Моля, въведете съобщение');
      return;
    }

    if (!privateRecipient) {
      toast.error('Моля, изберете получател');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('content', privateContent);
      formData.append('message_type', 'individual');
      formData.append('recipient_id', privateRecipient);
      
      if (privateFiles && privateFiles.length > 0) {
        Array.from(privateFiles).forEach(file => {
          formData.append('file', file);
        });
      }

      await axios.post(`${API_URL}/messages`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      toast.success('Съобщението е изпратено');
      setPrivateContent('');
      setPrivateRecipient('');
      setPrivateFiles([]);
      fetchData();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Грешка при изпращане на съобщението');
    }
  };

  // Lesson download is now handled by LessonFiles component

  // handleDownloadMessageFile is now handled by MessageFiles component

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете това съобщение?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Съобщението е изтрито успешно');
      fetchData();
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error(error.response?.data?.detail || 'Грешка при изтриване на съобщението');
    }
  };

  const handleEditMessage = (msg) => {
    setEditingMessage(msg);
    setEditMessageContent(msg.content || '');
    setEditMessageFilesToRemove(new Set());
    setEditMessageNewFiles([]);
    setEditMessageOpen(true);
  };

  const handleUpdateMessage = async (e) => {
    e.preventDefault();
    if (!editMessageContent.trim()) { toast.error('Съобщението не може да е празно'); return; }
    try {
      const formData = new FormData();
      formData.append('content', editMessageContent);
      formData.append('remove_file_indices', Array.from(editMessageFilesToRemove).join(','));
      editMessageNewFiles.forEach(f => formData.append('file', f));
      const res = await axios.put(`${API_URL}/messages/${editingMessage.message_id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Съобщението е обновено успешно');
      setEditMessageOpen(false);
      const updatedMsg = res.data;
      setBoardMessages(prev => prev.map(m => m.message_id === updatedMsg.message_id ? updatedMsg : m));
      setPrivateMessages(prev => prev.map(m => m.message_id === updatedMsg.message_id ? updatedMsg : m));
    } catch (error) {
      toast.error('Грешка при обновяване на съобщението');
    }
  };

  const getSenderRoleColor = (role) => {
    if (role === 'teacher') return 'bg-blue-100 text-blue-700';
    if (role === 'student') return 'bg-green-100 text-green-700';
    if (role === 'administrator') return 'bg-purple-100 text-purple-700';
    return 'bg-slate-100 text-slate-700';
  };

  const isImageFile = (filePath) => {
    if (!filePath) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    return imageExtensions.some(ext => filePath.toLowerCase().endsWith(ext));
  };

  const getPaymentBadge = () => {
    if (paymentStatus === 'paid') {
      return (
        <Badge data-testid="payment-badge-paid" className="bg-green-100 text-green-700 border-green-200 text-sm font-medium px-3 py-1">
          Платена Такса
        </Badge>
      );
    } else if (paymentStatus === 'unpaid') {
      return (
        <Badge data-testid="payment-badge-unpaid" className="bg-red-100 text-red-700 border-red-200 text-sm font-medium px-3 py-1">
          Неплатена Такса
        </Badge>
      );
    }
    return null;
  };

  const getUserClassName = () => {
    if (user.class_ids && user.class_ids.length > 0) {
      const userClass = classes.find(c => c.class_id === user.class_ids[0]);
      return userClass ? userClass.name : 'Няма клас';
    }
    return 'Няма клас';
  };

  const filteredLessons = lessons.filter(lesson => {
    if (lessonFilter === 'all') return true;
    // Add more filter logic as needed
    return true;
  });

  const filteredBoardMessages = boardMessages.filter(msg => {
    if (boardFilter === 'all') return true;
    if (boardFilter === 'teacher') return msg.sender_role === 'teacher';
    if (boardFilter === 'admin') return msg.sender_role === 'administrator';
    return true;
  });

  const filteredPrivateMessages = privateMessages.filter(msg => {
    if (privateFilter === 'all') return true;
    if (privateFilter === 'teacher') return msg.sender_role === 'teacher';
    if (privateFilter === 'admin') return msg.sender_role === 'administrator';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Зареждане...</div>
      </div>
    );
  }

  const ctx = {
    user,
    token,
    classes,
    lessons,
    filteredLessons,
    lessonFilter,
    setLessonFilter,
    boardContent,
    setBoardContent,
    boardFiles,
    setBoardFiles,
    boardFilter,
    setBoardFilter,
    filteredBoardMessages,
    boardMessages,
    handleSendBoardMessage,
    privateContent,
    setPrivateContent,
    privateRecipient,
    setPrivateRecipient,
    privateFiles,
    setPrivateFiles,
    privateFilter,
    setPrivateFilter,
    filteredPrivateMessages,
    privateMessages,
    handleSendPrivateMessage,
    recipients,
    getSenderRoleColor,
    handleEditMessage,
    handleDeleteMessage,
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F8EDE2'}}>
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          {/* Desktop header */}
          <div className="hidden sm:flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">БНУ Азбукарче</h1>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-sm text-slate-600">Ученик: {getFullName(user)}</p>
                  <Badge variant="outline" className="text-xs">
                    <Info className="h-3 w-3 mr-1" />
                    {getUserClassName()}
                  </Badge>
                  {user?.education_type === 'online' && (
                    <Badge data-testid="online-education-badge" className="text-xs bg-indigo-100 text-indigo-700 border-indigo-200 ml-1">
                      Онлайн обучение
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getPaymentBadge()}
              <NotificationBell token={token} />
              <ChangePasswordDialog token={token} />
              <Button
                onClick={() => { logout(); navigate('/'); }}
                variant="outline"
                data-testid="logout-btn"
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Изход
              </Button>
            </div>
          </div>

          {/* Mobile header */}
          <div className="flex sm:hidden flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div>
                  <h1 className="text-lg font-bold text-slate-900">БНУ Азбукарче</h1>
                  <p className="text-xs text-slate-600">{user.username}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <NotificationBell token={token} />
                <ChangePasswordDialog token={token} />
                <Button
                  onClick={() => { logout(); navigate('/'); }}
                  variant="outline"
                  size="sm"
                  data-testid="logout-btn"
                  className="flex items-center gap-1"
                >
                  <LogOut className="h-3 w-3" />
                  <span className="text-xs">Изход</span>
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs">
                <Info className="h-3 w-3 mr-1" />
                {getUserClassName()}
              </Badge>
              {getPaymentBadge()}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {user?.education_type === 'online' && (
          <div className="mb-6">
            <DirectDebitCard />
          </div>
        )}
        <Tabs defaultValue="lessons" className="space-y-6">
          <TabsList data-testid="student-tabs" className="flex flex-wrap w-full h-auto gap-1 p-1 bg-slate-100 rounded-lg">
            <TabsTrigger value="lessons" data-testid="lessons-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Записи</span>
            </TabsTrigger>
            <TabsTrigger value="board" data-testid="board-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <MessageSquare className="h-4 w-4" />
              <span>Табло</span>
            </TabsTrigger>
            <TabsTrigger value="private" data-testid="private-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <MessageSquare className="h-4 w-4" />
              <span>Лични</span>
            </TabsTrigger>
            <TabsTrigger value="attendance" data-testid="attendance-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <ClipboardList className="h-4 w-4" />
              <span>Присъствия</span>
            </TabsTrigger>
            <TabsTrigger value="competencies" data-testid="competencies-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <Award className="h-4 w-4" />
              <span>Комп.</span>
            </TabsTrigger>
            <TabsTrigger value="online" data-testid="online-tab" className="flex-1 min-w-[calc(33%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-2 text-[11px] sm:text-sm">
              <Video className="h-4 w-4" />
              <span>Онлайн</span>
            </TabsTrigger>
          </TabsList>

          <LessonsTab ctx={ctx} />

          <BoardTab ctx={ctx} />

          <PrivateTab ctx={ctx} />

          <TabsContent value="attendance" data-testid="attendance-content">
            <MyAttendancePage token={token} />
          </TabsContent>

          <TabsContent value="competencies" data-testid="competencies-content">
            <MyCompetenciesPage token={token} studentId={user.user_id} classIds={user.class_ids || []} />
          </TabsContent>

          <TabsContent value="online" data-testid="online-content">
            <OnlineLessonsPanel role="student" />
          </TabsContent>
        </Tabs>
      </main>

      {/* Message Edit Dialog */}
      <Dialog open={editMessageOpen} onOpenChange={setEditMessageOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Редактиране на съобщение</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateMessage} className="space-y-4">
            <div>
              <Label>Съдържание</Label>
              <div className="mt-1">
                <RichTextEditor value={editMessageContent} onChange={setEditMessageContent} placeholder="Въведете съобщение..." />
              </div>
            </div>
            {editingMessage?.file_paths?.length > 0 && (
              <div>
                <Label className="text-slate-600 text-sm">Прикачени файлове</Label>
                <div className="mt-1 space-y-1.5">
                  {editingMessage.file_paths.map((fp, i) => {
                    const name = editingMessage.file_names?.[i] || 'файл';
                    const removed = editMessageFilesToRemove.has(i);
                    return (
                      <div key={fp || `file-${i}`} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${removed ? 'bg-red-50 border-red-200 line-through text-slate-400' : 'bg-slate-50 border-slate-200'}`}>
                        <span className="truncate">{name}</span>
                        <button type="button"
                          onClick={() => setEditMessageFilesToRemove(prev => {
                            const next = new Set(prev);
                            if (next.has(i)) next.delete(i); else next.add(i);
                            return next;
                          })}
                          className={`ml-2 text-xs flex-shrink-0 px-2 py-0.5 rounded ${removed ? 'text-green-600 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'}`}
                        >{removed ? 'Върни' : 'Премахни'}</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <Label className="mb-2 block">Добави нови файлове</Label>
              <FilePickerInput files={editMessageNewFiles} onChange={setEditMessageNewFiles} label="Прикачи файлове" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditMessageOpen(false)}>Отказ</Button>
              <Button type="submit" className="flex-1 bg-[#00966E] hover:bg-[#007a59] text-white">Запази</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
